import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Loader2, LogIn, UserPlus, ArrowLeft, Mail, UtensilsCrossed, Hotel, TreePine } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { trackActivity, syncPendingEvents } from '@/services/activityTrackingService';

interface AuthFormValues {
  email: string;
  password: string;
}

interface ResetPasswordFormValues {
  email: string;
}

const SimpleAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetPasswordSent, setResetPasswordSent] = useState(false);
  const [establishmentMode, setEstablishmentMode] = useState<'restaurant' | 'hotel' | 'camping' | null>(null);
  
  const intent = searchParams.get('intent');
  const isMagic = searchParams.get('magic') === 'true';
  const emailFromUrl = searchParams.get('email');

  useEffect(() => {
    if (intent === 'ocr' || isMagic) {
      setActiveTab('signup');
    }
  }, [intent, isMagic]);
  
  const form = useForm<AuthFormValues>({
    defaultValues: {
      email: emailFromUrl || '',
      password: '',
    },
  });

  useEffect(() => {
    if (emailFromUrl) {
      form.setValue('email', emailFromUrl);
    }
  }, [emailFromUrl, form]);

  const resetForm = useForm<ResetPasswordFormValues>({
    defaultValues: {
      email: '',
    },
  });

  const onLogin = async (values: AuthFormValues) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        // Store establishment mode for sector routing
        if (establishmentMode === 'hotel' || establishmentMode === 'camping') {
          localStorage.setItem('establishment_mode', establishmentMode);
        }
        await trackActivity('login', { source: isMagic ? 'magic' : 'direct' });
        await syncPendingEvents(data.user.id);
        
        toast.success('Connexion réussie !');
        // Check establishment mode for sector-specific redirect
        const mode = establishmentMode || localStorage.getItem('establishment_mode');
        let redirectPath: string;
        if (mode === 'camping') {
          redirectPath = '/camping-setup';
        } else if (mode === 'hotel') {
          redirectPath = '/setup-restaurant?mode=hotel';
        } else {
          redirectPath = getRedirectPath();
        }
        navigate(redirectPath, { replace: true });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const getRedirectPath = () => {
    if (isMagic) {
      return '/setup-restaurant';
    }
    
    if (intent === 'ocr') {
      return '/dashboard/menu-ocr';
    }
    
    const postAuthRedirect = localStorage.getItem('postAuthRedirect');
    if (postAuthRedirect) {
      localStorage.removeItem('postAuthRedirect');
      return `/dashboard/${postAuthRedirect}`;
    }
    
    return '/dashboard';
  };

  const onSignup = async (values: AuthFormValues) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const redirectUrl = `${window.location.origin}${getRedirectPath()}`;
      
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            establishment_mode: establishmentMode || 'restaurant',
          },
          emailRedirectTo: redirectUrl
        }
      });
      
      if (error) throw error;
      
      if (data.user) {
        // Store establishment mode for post-signup routing
        if (establishmentMode === 'hotel' || establishmentMode === 'camping') {
          localStorage.setItem('establishment_mode', establishmentMode);
        }
        
        await trackActivity('signup', { source: isMagic ? 'magic' : 'direct', mode: establishmentMode });
        await syncPendingEvents(data.user.id);
        
        if (data.user.email_confirmed_at) {
          toast.success('Compte créé ! Connexion automatique...');
          const redirectPath = establishmentMode === 'hotel' ? '/setup-restaurant?mode=hotel' : establishmentMode === 'camping' ? '/camping-setup' : getRedirectPath();
          navigate(redirectPath, { replace: true });
        } else {
          toast.success('Compte créé ! Vérifiez votre email pour confirmer votre compte.');
        }
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      toast.error(error.message || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values: AuthFormValues) => {
    if (activeTab === 'login') {
      await onLogin(values);
    } else {
      await onSignup(values);
    }
  };

  const onForgotPassword = async (values: ResetPasswordFormValues) => {
    if (loading) return;
    
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      setResetPasswordSent(true);
      toast.success('Email de réinitialisation envoyé ! Vérifiez votre boîte mail.');
    } catch (error: any) {
      console.error('Reset password error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetPasswordSent(false);
    resetForm.reset();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0a0a0f]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <UtensilsCrossed className="h-6 w-6 text-orange-500" />
            <span className="text-xl font-bold text-white">Miam.food</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">
                {showForgotPassword 
                  ? 'Mot de passe oublié' 
                  : intent === 'ocr' 
                    ? 'Créez votre menu OCR' 
                    : 'Bienvenue'
                }
              </h1>
              <p className="text-gray-400">
                {showForgotPassword 
                  ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                  : intent === 'ocr' 
                    ? 'Créez un compte pour sauvegarder votre menu digital'
                    : 'Connectez-vous ou créez un compte'
                }
              </p>
            </div>

            {showForgotPassword ? (
              // Forgot Password Form
              <div className="space-y-4">
                {resetPasswordSent ? (
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 mx-auto bg-orange-500/20 rounded-full flex items-center justify-center">
                      <Mail className="w-8 h-8 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Email envoyé !</h3>
                    <p className="text-gray-400">
                      Vérifiez votre boîte mail et cliquez sur le lien pour réinitialiser votre mot de passe.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={handleBackToLogin}
                      className="w-full border-white/20 text-white hover:bg-white/10"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Retour à la connexion
                    </Button>
                  </div>
                ) : (
                  <Form {...resetForm}>
                    <form onSubmit={resetForm.handleSubmit(onForgotPassword)} className="space-y-4">
                      <FormField
                        control={resetForm.control}
                        name="email"
                        rules={{ 
                          required: 'Email requis',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Email invalide'
                          }
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-gray-300">Email</FormLabel>
                            <FormControl>
                              <Input 
                                type="email" 
                                placeholder="votre@email.com" 
                                className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <Button 
                        type="submit" 
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white" 
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <Mail className="mr-2 h-4 w-4" />
                            Envoyer le lien
                          </>
                        )}
                      </Button>
                      
                      <Button 
                        type="button"
                        variant="ghost" 
                        onClick={handleBackToLogin}
                        className="w-full text-gray-400 hover:text-white hover:bg-white/10"
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Retour à la connexion
                      </Button>
                    </form>
                  </Form>
                )}
              </div>
            ) : (
              // Normal Auth Form
              <div className="space-y-6">
                <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'login' | 'signup')}>
                  <TabsList className="grid w-full grid-cols-2 bg-white/5 border border-white/10">
                    <TabsTrigger 
                      value="login"
                      className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400"
                    >
                      Connexion
                    </TabsTrigger>
                    <TabsTrigger 
                      value="signup"
                      className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-gray-400"
                    >
                      Inscription
                    </TabsTrigger>
                  </TabsList>
                  
                  <div className="mt-6">
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                          control={form.control}
                          name="email"
                          rules={{ 
                            required: 'Email requis',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Email invalide'
                            }
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-300">Email</FormLabel>
                              <FormControl>
                                <Input 
                                  type="email" 
                                  placeholder="votre@email.com" 
                                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name="password"
                          rules={{ 
                            required: 'Mot de passe requis',
                            minLength: {
                              value: 6,
                              message: 'Minimum 6 caractères'
                            }
                          }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-gray-300">Mot de passe</FormLabel>
                              <FormControl>
                                <Input 
                                  type="password" 
                                  placeholder="••••••••" 
                                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-orange-500"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <TabsContent value="login" className="mt-6 space-y-4">
                          <Button 
                            type="submit" 
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white" 
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Connexion...
                              </>
                            ) : (
                              <>
                                <LogIn className="mr-2 h-4 w-4" />
                                Se connecter
                              </>
                            )}
                          </Button>
                          
                          <button 
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="w-full text-center text-sm text-orange-500 hover:text-orange-400 transition-colors"
                          >
                            Mot de passe oublié ?
                          </button>
                        </TabsContent>
                        
                        <TabsContent value="signup" className="mt-6 space-y-4">
                          {/* Establishment type selector */}
                          <div className="space-y-2">
                            <p className="text-sm text-gray-400 text-center">Vous êtes :</p>
                          <div className="grid grid-cols-3 gap-3">
                              <button
                                type="button"
                                onClick={() => setEstablishmentMode('restaurant')}
                                className={`p-3 rounded-xl border-2 transition-all text-center ${
                                  establishmentMode === 'restaurant' 
                                    ? 'border-orange-500 bg-orange-500/10 text-white' 
                                    : 'border-white/10 text-gray-400 hover:border-white/30'
                                }`}
                              >
                                <UtensilsCrossed className="h-5 w-5 mx-auto mb-1" />
                                <span className="text-xs font-medium">Restaurant</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setEstablishmentMode('hotel')}
                                className={`p-3 rounded-xl border-2 transition-all text-center ${
                                  establishmentMode === 'hotel' 
                                    ? 'border-orange-500 bg-orange-500/10 text-white' 
                                    : 'border-white/10 text-gray-400 hover:border-white/30'
                                }`}
                              >
                                <Hotel className="h-5 w-5 mx-auto mb-1" />
                                <span className="text-xs font-medium">Hôtel</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setEstablishmentMode('camping')}
                                className={`p-3 rounded-xl border-2 transition-all text-center ${
                                  establishmentMode === 'camping' 
                                    ? 'border-orange-500 bg-orange-500/10 text-white' 
                                    : 'border-white/10 text-gray-400 hover:border-white/30'
                                }`}
                              >
                                <TreePine className="h-5 w-5 mx-auto mb-1" />
                                <span className="text-xs font-medium">Camping</span>
                              </button>
                            </div>
                          </div>

                          <Button 
                            type="submit" 
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white" 
                            disabled={loading || !establishmentMode}
                          >
                            {loading ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Création...
                              </>
                            ) : (
                              <>
                                <UserPlus className="mr-2 h-4 w-4" />
                                Créer un compte
                              </>
                            )}
                          </Button>
                        </TabsContent>
                      </form>
                    </Form>
                  </div>
                </Tabs>
              </div>
            )}
            
            {/* Back to home */}
            <div className="mt-8 text-center">
              <Link 
                to="/"
                className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
              >
                ← Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleAuth;
